import * as React from 'react';

type ScrollDirection = 'horizontal' | 'vertical';

type ScrollControls = {
    ref: React.RefObject<HTMLDivElement>;
    scroll: (elementId: number) => void;
    scrollPosition: number;
    scrollToLast: (skip?: number) => void;
    scrollToNext: (skip?: number) => void;
    isAtStart: boolean;
    isAtEnd: boolean;
};

export default function useScrollControl(
    scrollDirection: ScrollDirection,
    scrollOffset = 0,
): ScrollControls {
    const ref = React.useRef<HTMLDivElement>(null);
    const [scrollElementId, setScrollElementId] = React.useState(0);
    const [scrollPosition, setScrollPosition] = React.useState(0);
    const [isAtStart, setIsAtStart] = React.useState(true);
    const [isAtEnd, setIsAtEnd] = React.useState(false);

    const handleScroll = React.useCallback(() => {
        if (!ref.current) return;

        const currentScroll =
            scrollDirection === 'horizontal'
                ? ref.current.scrollLeft
                : ref.current.scrollTop;
        const maxScroll =
            (scrollDirection === 'horizontal'
                ? ref.current.scrollWidth - ref.current.clientWidth
                : ref.current.scrollHeight - ref.current.clientHeight) || 0;

        setScrollPosition(currentScroll);

        setIsAtStart(currentScroll <= 0);
        // if the component isn't big enough to scroll, treat it as if it was the end
        setIsAtEnd(currentScroll >= maxScroll - 1 || maxScroll <= 0);
    }, [scrollDirection]);

    React.useEffect(() => {
        const elem = ref.current;
        if (!elem) return () => {};

        requestAnimationFrame(() => {
            setTimeout(() => {
                handleScroll();
            }, 0);
        });

        const observer = new MutationObserver(() => {
            handleScroll();
        });

        observer.observe(elem, { childList: true, subtree: true });

        elem.addEventListener('scroll', handleScroll);

        return () => {
            observer.disconnect();
            elem.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    const scroll = (elementId: number) => {
        if (ref.current) {
            const childRef = ref.current.children.item(elementId);
            if (childRef) {
                const parentRect = ref.current.getBoundingClientRect();

                const childRect = childRef.getBoundingClientRect();
                const scrollTo =
                    childRect[
                        scrollDirection === 'horizontal' ? 'left' : 'top'
                    ] -
                    parentRect[
                        scrollDirection === 'horizontal' ? 'left' : 'top'
                    ];
                ref.current?.scrollBy({
                    [scrollDirection === 'horizontal' ? 'left' : 'top']:
                        scrollTo - scrollOffset,
                    behavior: 'smooth',
                });
                const scrollEvent = new Event('scroll');
                ref.current.dispatchEvent(scrollEvent);
            }
        }
        setScrollElementId(elementId);
    };

    const scrollToLast = (skip = 0) => {
        if (!ref.current) return;

        const children = Array.from(ref.current.children);
        if (scrollElementId <= 0) return;

        const prevElement = children[scrollElementId - (1 + skip)];
        if (prevElement) {
            prevElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }

        setScrollElementId((prev) => Math.max(prev - (1 + skip), 0));
    };

    const scrollToNext = (skip = 0) => {
        if (!ref.current) return;

        const children = Array.from(ref.current.children);
        if (scrollElementId >= children.length - 1) return;

        const nextElement = children[scrollElementId + (1 + skip)];
        if (nextElement) {
            nextElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }

        setScrollElementId((prev) =>
            Math.min(prev + (1 + skip), children.length - 1),
        );
    };

    return {
        ref,
        scroll,
        scrollPosition,
        scrollToLast,
        scrollToNext,
        isAtStart,
        isAtEnd,
    };
}
